import React, { useEffect, useState } from 'react';
import Portal from '../../Portal/Portal';
import InteractiveButton from '../../InteractiveButton/InteractiveButton';
import { ButtonType } from '../../../types/button-type';
import { toZoomedPdfUrl } from './pdfViewerUrl';

export interface PdfViewerPage {
  id: string;
  image: string;
  isIncluded: boolean;
}

interface PdfPagesViewerModalProps {
  isVisible: boolean;
  title: string;
  pages: PdfViewerPage[];
  initialPageId: string | null;
  onClose: () => void;
  onToggleInclude: (pageId: string, isIncluded: boolean) => void;
}

// Full-screen viewer for a single uploaded PDF's pages. Opens on a thumbnail
// grid of every page (same iframe-of-the-signed-URL technique as the small
// cards on the report page, just bigger), and clicking a thumbnail zooms
// into that one page. Every page carries the include/exclude checkbox used
// to build the final report, plus Select all / Unselect all shortcuts.
// Nothing is ever downloaded - it's the same S3 URL rendered at a different
// size.
const PdfPagesViewerModal: React.FC<PdfPagesViewerModalProps> = ({
  isVisible,
  title,
  pages,
  initialPageId,
  onClose,
  onToggleInclude,
}) => {
  // Tracked locally rather than driven straight off `pages` - the redux
  // state behind that prop isn't updated after updateReportInclude resolves
  // (see store/models/reports.ts), so a checkbox bound directly to it would
  // appear to snap back right after being toggled.
  const [includedById, setIncludedById] = useState<Record<string, boolean>>({});
  const [zoomedPageId, setZoomedPageId] = useState<string | null>(null);

  useEffect(() => {
    if (isVisible) {
      setIncludedById(Object.fromEntries(pages.map((page) => [page.id, page.isIncluded])));
      setZoomedPageId(initialPageId && pages.some((page) => page.id === initialPageId) ? initialPageId : null);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (zoomedPageId) {
          setZoomedPageId(null);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isVisible, zoomedPageId, onClose]);

  if (!isVisible) return null;

  const handleToggle = (pageId: string, checked: boolean) => {
    setIncludedById((prev) => ({ ...prev, [pageId]: checked }));
    onToggleInclude(pageId, checked);
  };

  const handleSelectAll = (checked: boolean) => {
    setIncludedById(Object.fromEntries(pages.map((page) => [page.id, checked])));
    pages.forEach((page) => onToggleInclude(page.id, checked));
  };

  const zoomedIndex = zoomedPageId ? pages.findIndex((page) => page.id === zoomedPageId) : -1;
  const zoomedPage = zoomedIndex >= 0 ? pages[zoomedIndex] : null;

  return (
    <Portal>
      <div className="fixed inset-0 z-9999 flex flex-col bg-black/95">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-strokedark px-4 py-3 sm:px-6">
          <h3 className="truncate text-base font-semibold text-white sm:text-lg">{title}</h3>
          <div className="flex items-center gap-2">
            <InteractiveButton type={ButtonType.PrimaryInverseWhite} text="Select all" onClick={() => handleSelectAll(true)} />
            <InteractiveButton type={ButtonType.PrimaryInverseWhite} text="Unselect all" onClick={() => handleSelectAll(false)} />
            <InteractiveButton type={ButtonType.Primary} text="Close" onClick={onClose} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8">
          {zoomedPage ? (
            <div className="mx-auto flex max-w-4xl flex-col items-center gap-4">
              <div className="flex w-full max-w-[800px] items-center justify-between">
                <InteractiveButton
                  type={ButtonType.PrimaryInverseWhite}
                  text="Back to all pages"
                  onClick={() => setZoomedPageId(null)}
                />
                <div className="flex items-center gap-2">
                  <InteractiveButton
                    type={ButtonType.PrimaryInverseWhite}
                    text="‹ Prev"
                    disabled={zoomedIndex <= 0}
                    onClick={() => setZoomedPageId(pages[zoomedIndex - 1].id)}
                  />
                  <InteractiveButton
                    type={ButtonType.PrimaryInverseWhite}
                    text="Next ›"
                    disabled={zoomedIndex >= pages.length - 1}
                    onClick={() => setZoomedPageId(pages[zoomedIndex + 1].id)}
                  />
                </div>
              </div>
              <label className="flex cursor-pointer select-none items-center gap-2 self-start rounded bg-white px-3 py-1.5 text-sm font-medium text-black shadow">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer"
                  checked={!!includedById[zoomedPage.id]}
                  onChange={(event) => handleToggle(zoomedPage.id, event.target.checked)}
                />
                Page {zoomedIndex + 1} &ndash; Include in report
              </label>
              <div
                className="w-full overflow-hidden rounded border border-strokedark bg-white shadow-lg"
                style={{ maxWidth: 800, aspectRatio: '8.5 / 11' }}
              >
                <iframe src={toZoomedPdfUrl(zoomedPage.image)} title={`Page ${zoomedIndex + 1}`} className="h-full w-full border-none" />
              </div>
            </div>
          ) : (
            <div
              className="mx-auto grid max-w-6xl gap-4"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
            >
              {pages.map((page, index) => (
                <div
                  key={page.id}
                  className="relative cursor-pointer overflow-hidden rounded border border-strokedark bg-white shadow-lg"
                  style={{ aspectRatio: '8.5 / 11' }}
                  onClick={() => setZoomedPageId(page.id)}
                >
                  <iframe
                    src={page.image}
                    title={`Page ${index + 1}`}
                    className="h-full w-full border-none"
                    style={{ pointerEvents: 'none' }}
                  />
                  <label
                    className="absolute left-2 top-2 z-10 flex cursor-pointer select-none items-center gap-1.5 rounded bg-white px-2 py-1 text-xs font-medium text-black shadow"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 cursor-pointer"
                      checked={!!includedById[page.id]}
                      onChange={(event) => handleToggle(page.id, event.target.checked)}
                    />
                    Include
                  </label>
                  <div className="absolute bottom-2 left-2 z-10 rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white">
                    Page {index + 1}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default PdfPagesViewerModal;
